'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_MANUAL_HANDEDNESS,
  FULL_MANUAL_CAPABILITIES,
  MANUAL_BATTERY_WARN_LEVEL,
  deviceClassFor,
  detectManualCapabilities,
  isPortrait,
  manualInputRefusal,
  pointerModeFor,
  readManualHandedness,
  writeManualHandedness,
  type ManualCapabilities,
  type ManualDeviceClass,
  type ManualHandedness,
} from '@/lib/manual-touch';
import { MANUAL_REFUSAL, type PointerMode } from '@/lib/manual-vector';

/**
 * What the operator is holding, and what it can do.
 *
 * Every rule this hook applies is a pure function in `lib/manual-touch.ts`; the
 * hook only measures. That split is deliberate — the classification rules are
 * the part that has to be right, and they are testable without a DOM.
 *
 * Measured AFTER mount, never during render, for the same reason
 * `useManualStageLayout` reads its storage in an effect: the server has no
 * `matchMedia`, no `navigator` and no viewport, so deriving any of this during
 * the first render is a hydration mismatch. Until the first measurement lands
 * the answer is the permissive one — a desktop, in landscape, with everything
 * supported — because refusing manual flight on ignorance would flash a dead
 * MANUAL key on every load, and the surface itself re-checks anyway.
 */

export interface ManualDevice {
  pointer: PointerMode;
  deviceClass: ManualDeviceClass;
  /** True while the device is held upright. There is no portrait control. */
  portrait: boolean;
  capabilities: ManualCapabilities;
  /**
   * Why this device or browser cannot fly, or null. Fed to
   * `manualControlAvailability` so the MANUAL key is refused with a reason
   * instead of being offered into a surface that cannot work.
   */
  refusal: string | null;
  /** Battery level 0–1 while below the warn threshold and not charging; null otherwise. */
  batteryLevel: number | null;
  /** True once a real measurement has replaced the server-render defaults. */
  measured: boolean;
}

const SSR_DEVICE: ManualDevice = {
  pointer: 'desktop',
  deviceClass: 'desktop',
  portrait: false,
  capabilities: FULL_MANUAL_CAPABILITIES,
  refusal: null,
  batteryLevel: null,
  measured: false,
};

interface BatteryLike extends EventTarget {
  level: number;
  charging: boolean;
}

export function useManualDevice(): ManualDevice {
  const [device, setDevice] = useState<ManualDevice>(SSR_DEVICE);

  // The battery is answered asynchronously and on its own events, so it is held
  // apart and merged in — otherwise a `levelchange` would have to re-measure the
  // viewport, and a resize would have to re-await the battery.
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);

  useEffect(() => {
    const capabilities = detectManualCapabilities(window);

    const measure = () => {
      const viewport = {
        widthPx: window.innerWidth,
        heightPx: window.innerHeight,
        coarsePointer:
          typeof window.matchMedia === 'function'
            ? window.matchMedia('(pointer: coarse)').matches
            : false,
      };
      const deviceClass = deviceClassFor(viewport);
      // Ordered: a browser that cannot run the control at all is a different
      // problem from a device that is the wrong shape, and the operator can only
      // act on one of them at a time.
      const refusal =
        manualInputRefusal(capabilities)
        ?? (deviceClass === 'phone' ? MANUAL_REFUSAL.viewportTooNarrow : null);

      setDevice((prev) => {
        const next: ManualDevice = {
          pointer: pointerModeFor(viewport),
          deviceClass,
          portrait: isPortrait(viewport),
          capabilities,
          refusal,
          batteryLevel: prev.batteryLevel,
          measured: true,
        };
        return prev.pointer === next.pointer
          && prev.deviceClass === next.deviceClass
          && prev.portrait === next.portrait
          && prev.refusal === next.refusal
          && prev.measured
          ? prev
          : next;
      });
    };

    measure();

    // `orientationchange` alone is not enough (a desktop never fires it) and
    // `resize` alone lags on some mobile browsers, so both drive the same
    // measurement, which is idempotent.
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    // A pointer can genuinely change kind mid-session: a tablet with a keyboard
    // folio attached, a laptop with a touchscreen the operator starts using.
    const coarse =
      typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null;
    coarse?.addEventListener?.('change', measure);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      coarse?.removeEventListener?.('change', measure);
    };
  }, []);

  // Battery Status API. Absent on Safari and on Firefox, and that is not a
  // degraded state to announce — a warning nobody can measure is not a warning,
  // so this simply produces nothing.
  useEffect(() => {
    const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryLike> };
    if (typeof nav.getBattery !== 'function') return;

    let cancelled = false;
    let battery: BatteryLike | null = null;
    const onChange = () => {
      if (cancelled || !battery) return;
      setBatteryLevel(
        !battery.charging && battery.level < MANUAL_BATTERY_WARN_LEVEL ? battery.level : null,
      );
    };

    void nav
      .getBattery()
      .then((b) => {
        if (cancelled) return;
        battery = b;
        b.addEventListener('levelchange', onChange);
        b.addEventListener('chargingchange', onChange);
        onChange();
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      battery?.removeEventListener('levelchange', onChange);
      battery?.removeEventListener('chargingchange', onChange);
    };
  }, []);

  // Merged here rather than in the battery effect so the two measurements stay
  // independent and neither has to re-run for the other's sake. Memoised so the
  // merge does not hand out a fresh object on every render of the COP.
  return useMemo(
    () => (device.batteryLevel === batteryLevel ? device : { ...device, batteryLevel }),
    [device, batteryLevel],
  );
}

/**
 * Which bottom corner the control takes, remembered between sessions.
 *
 * Per operator and read after mount, exactly like `useManualStageLayout` — the
 * same reasoning applies twice: a shared console must not blend two operators'
 * choices, and `localStorage` cannot be read during a server render.
 */
export function useManualHandedness({ operator = null }: { operator?: string | null } = {}): {
  handed: ManualHandedness;
  setHanded: (next: ManualHandedness) => void;
  /** False when the choice could not be persisted, so the caller can say so. */
  persisted: boolean;
} {
  const [handed, setHandedState] = useState<ManualHandedness>(DEFAULT_MANUAL_HANDEDNESS);
  const [persisted, setPersisted] = useState(true);
  const handedRef = useRef(handed);
  handedRef.current = handed;

  useEffect(() => {
    const stored = readManualHandedness(operator);
    handedRef.current = stored;
    setHandedState(stored);
  }, [operator]);

  const setHanded = useCallback(
    (next: ManualHandedness) => {
      if (handedRef.current === next) return;
      handedRef.current = next;
      setPersisted(writeManualHandedness(next, operator));
      setHandedState(next);
    },
    [operator],
  );

  return { handed, setHanded, persisted };
}
